/**
 * Where a pinned row sits under the app header.
 *
 * WHY THIS IS A MODULE. The offset was written out by hand in five places, and
 * one of them was wrong: the Calendar's event filter pinned at +56px while
 * everything else pinned at +47px, leaving a 9px sliver of page background
 * scrolling through the gap above it. The Dex had the SAME bug and was fixed in
 * isolation (PR-E8 fix 3), so the correction never reached the other copies —
 * which is exactly what a repeated magic number does. Reported on device
 * 2026-08-12.
 *
 * The measurement itself: AppShell's sticky header is `py-2` (16px) + a 30px
 * logo + a 1px bottom border = 47px. Anything pinning flush against it uses
 * HEADER_H; anything pinning below a filter row adds that row's height.
 */

/**
 * AppShell's sticky header: py-2 (16) + 30px logo + 1px border = 47px ON PAPER.
 *
 * Only a fallback. AppShell MEASURES the real header and publishes it as
 * `--app-header-h`, because the paper number is wrong the moment anything makes
 * the header taller — a PRO pill, a larger dynamic-type setting, a page's
 * `rightSlot`. When it was wrong the pinned rows cut into the header, and
 * because they out-stack it they drew over the chrome. Reported on device
 * 2026-08-12.
 */
export const HEADER_H = 47;

/** The measured header height, falling back to the paper value. */
const HEADER = `var(--app-header-h, ${HEADER_H}px)`;

/**
 * Stacking for a pinned filter row.
 *
 * BELOW the header (which is z-30), never above. These rows were z-40, so any
 * disagreement between their offset and the header's real height showed up as
 * the row painting ON TOP of the chrome. Under it, the worst case is a row
 * tucking half a pixel out of sight — which nobody can see.
 */
export const STICKY_ROW_Z = 20;

/** A band pinned directly beneath a filter row sits one below it. */
export const STICKY_BAND_Z = 19;

/**
 * A pinned filter row's own height: py-2 (16px) + a 36px pill + its 1px bottom
 * border. The band below one (the Dex ActionBar) stacks on this so the two read
 * as one continuous surface with no seam.
 * (2026-08-21: briefly 57 then 61 then 65 while the rows grew padding chasing
 * "pills clipped under the header"; the real culprit was the iOS sticky-header
 * flow-box mismatch — see .app-shell-column in index.css — so this is back to
 * the true 53.)
 */
export const FILTER_ROW_H = 53;

/** Pin flush against the app header. */
export const STICKY_UNDER_HEADER = `calc(var(--nx-safe-top) + ${HEADER})`;

/** Pin directly below a filter row that is itself pinned under the header. */
export const STICKY_UNDER_FILTER_ROW =
  `calc(var(--nx-safe-top) + ${HEADER} + ${FILTER_ROW_H}px)`;
